import React, { useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { formsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore, UserRole } from '../stores/authStore';

// Utility function to format numbers with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

// Helper function to get usage color
const getUsageColor = (currentUsage: number, packageLimit: number): string => {
  const percentage = (currentUsage / packageLimit) * 100;
  if (percentage >= 90) return 'text-red-600';
  if (percentage >= 75) return 'text-yellow-600';
  if (percentage >= 50) return 'text-orange-600';
  return 'text-green-600';
};

// Helper function to get monitor status color
const getMonitorColor = (status?: string): string => {
  switch (status?.toLowerCase()) {
    case 'monitoring':
      return 'bg-green-100 text-green-800';
    case 'paused':
      return 'bg-yellow-100 text-yellow-800';
    case 'stopped':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

interface ServiceForm {
  id: number;
  customerName: string;
  customerId?: string;
  serviceLine: string;
  kitNumber?: string;
  oldServiceLine?: string;
  packageLimit: number;
  oldPackage?: number;
  currentUsage: number;
  usagePercentage?: number;
  notified75?: boolean;
  notified100?: boolean;
  status: string;
  monitorStatus?: string;
  activationDate?: string;
  lastChecked: string;
  accountManager?: string;
  owner: {
    id: number;
    username: string;
    email: string;
  };
  isActive?: boolean;
}

const Sales: React.FC = () => {
  const [search, setSearch] = useState('');
  const [filterOption, setFilterOption] = useState<string>('all');
  const { user } = useAuthStore();
  const debounce = useRef<NodeJS.Timeout>();

  // React Query for forms data
  const { data: formsData } = useQuery(
    ['sales-forms'],
    () => formsAPI.getAllForms(),
    {
      onError: (error: any) => {
        console.error('Error fetching forms:', error);
        toast.error('Failed to load sales data');
      }
    }
  );

  // Client-side filtering
  // Handle paginated response structure from backend: { data: ServiceForm[], pagination: {...} }
  const formsArray: ServiceForm[] = Array.isArray(formsData)
    ? formsData
    : (formsData?.data && Array.isArray(formsData.data) ? formsData.data : []);

  const filtered = formsArray.filter((form: ServiceForm) => {
    const haystack = `${form.customerName} ${form.serviceLine} ${form.customerId || ''} ${form.kitNumber || ''}`.toLowerCase();
    const matches = haystack.includes(search.toLowerCase());
    return matches;
  });

  // Apply role-based and additional filters
  const displayedForms = filtered.filter((form: ServiceForm) => {
    // Role-based filtering
    if (user?.role === UserRole.ACCOUNT_MANAGER) {
      // Account Managers can only see forms assigned to them
      // Check both email and username for backward compatibility
      if (!form.accountManager || (form.accountManager !== user.email && form.accountManager !== user.username)) {
        return false;
      }
    } else if (user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN) {
      // Super Admins and Administrators can see all forms with assigned Account Managers
      if (!form.accountManager || form.accountManager.trim() === '') {
        return false;
      }
    } else {
      // Regular users can only see forms assigned to them
      // Check both email and username for backward compatibility
      if (!form.accountManager || (form.accountManager !== user?.email && form.accountManager !== user?.username)) {
        return false;
      }
    }
    
    // Then apply additional filters
    switch (filterOption) {
      case 'optedin':
        return form.status.toLowerCase() === 'opted in';
      case 'optedout':
        return form.status.toLowerCase() === 'opted out';
      case '75':
        return (form.currentUsage / form.packageLimit) >= 0.75;
      case 'active':
        return form.isActive;
      case 'inactive':
        return !form.isActive;
      default:
        return true;
    }
  });

  // Export forms to Excel via backend endpoint
  const handleExport = async () => {
    try {
      toast.loading('Preparing export... This may take a moment for large datasets.', { duration: 3000 });
      
      // Send current filters to backend
      const exportFilters = {
        search: search,
        filterOption: filterOption,
        role: user?.role
      };
      
      const blob = await formsAPI.exportForms(exportFilters);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales_data_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully 📄');
    } catch (error: any) {
      console.error('Export failed:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied: You do not have permission to export data.');
      } else if (error.response?.status === 422) {
        toast.error('Export failed: Dataset too large. Please try again in a few minutes.');
      } else if (error.response?.status === 429) {
        toast.error('Export failed: Rate limit exceeded. Please wait a moment and try again.');
      } else if (error.response?.status === 500) {
        toast.error('Export failed: Server error. Please try again later.');
      } else {
        toast.error('Failed to export sales data. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(34,197,94,0.25),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.22),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.12)]" />
              Sales view
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Sales</h1>
            <p className="max-w-2xl text-white/70">
              {user?.role === UserRole.ACCOUNT_MANAGER
                ? 'Manage your assigned customer accounts and service forms.'
                : user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN
                  ? 'Track performance and customer analytics across all Account Managers.'
                  : 'Track sales performance and customer analytics.'}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-users text-white/60"></i>
                {formatNumber(displayedForms.length)} records
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-filter text-white/60"></i>
                Filter: {filterOption}
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {!user?.hasAuditAccess && (
              <button
                onClick={handleExport}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
              >
                <i className="fas fa-file-excel"></i>
                Export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              className="w-full rounded-2xl bg-gray-50 px-5 py-3 pl-12 text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              placeholder="Search customers, service lines, kit numbers..."
              value={search}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                if (debounce.current) clearTimeout(debounce.current);
                debounce.current = setTimeout(() => setSearch(val), 500);
              }}
            />
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Filter Dropdown */}
            <select
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
              className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="all">All Records</option>
              <option value="optedin">Opted In</option>
              <option value="optedout">Opted Out</option>
              <option value="75">≥ 75% Usage</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(displayedForms.length)}</p>
              <p className="text-gray-600 font-medium text-sm">{search ? 'Found' : 'Total'} Sales</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
              <i className="fas fa-chart-bar text-lg"></i>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-gray-500 text-xs">Sales records</span>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(displayedForms.filter((f: ServiceForm) => f.isActive).length)}
              </p>
              <p className="text-gray-600 font-medium text-sm">Active</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg">
              <i className="fas fa-check-circle text-lg"></i>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex items-center text-green-600">
              <i className="fas fa-arrow-up text-xs mr-1"></i>
              <span className="text-xs font-medium">+5%</span>
            </div>
            <span className="text-gray-500 text-xs ml-2">this week</span>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(displayedForms.filter((f: ServiceForm) => !f.isActive).length)}
              </p>
              <p className="text-gray-600 font-medium text-sm">Inactive</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-lg">
              <i className="fas fa-pause-circle text-lg"></i>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-gray-500 text-xs">Suspended sales</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(displayedForms.filter((f: ServiceForm) => f.status.toLowerCase() === 'opted out').length)}
              </p>
              <p className="text-gray-600 font-medium text-sm">Opted Out</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
              <i className="fas fa-user-times text-lg"></i>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-gray-500 text-xs">Monitoring disabled</span>
          </div>
        </div>

        <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative">
          <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg absolute top-4 right-4">
            <i className="fas fa-chart-pie text-xl"></i>
          </div>
          <div className="pr-16">
            <div className="flex items-baseline space-x-2">
              <p className="text-4xl font-bold text-gray-900 whitespace-nowrap">
                {displayedForms
                  .reduce((acc: number, f: ServiceForm) => acc + f.currentUsage, 0)
                  .toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              </p>
              <span className="text-xl font-bold text-gray-900">GB</span>
            </div>
            <p className="text-gray-600 font-medium mt-2">Total Usage</p>
            <p className="text-gray-500 text-sm mt-1">Data consumed</p>
          </div>
        </div>
      </div>

      {/* Service Forms Table - Only forms with Account Managers */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-black text-gray-900">
                {user?.role === UserRole.ACCOUNT_MANAGER ? 'Your assigned forms' : 'Sales forms'}
              </h3>
              {displayedForms.length > 0 && (
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {displayedForms.length} records
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</div>
          </div>
        </div>
        
        <table className="w-full" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '20%' }}>
                Customer Info
              </th>
              <th className="px-3 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '15%' }}>
                Account Manager
              </th>
              <th className="px-3 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '20%' }}>
                Service Details
              </th>
              <th className="px-3 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '20%' }}>
                Package & Usage
              </th>
              <th className="px-3 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '10%' }}>
                Status
              </th>
              <th className="px-3 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '15%' }}>
                Monitor
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {displayedForms.map((form: ServiceForm, index: number) => (
              <tr key={form.id} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                <td className="px-3 py-6" style={{ width: '20%' }}>
                  <div>
                    <div className="text-sm font-bold text-gray-900">
                      {form.customerName}
                    </div>
                    {form.customerId && (
                      <div className="text-sm text-gray-500">
                        ID: {form.customerId}
                      </div>
                    )}
                    {form.activationDate && (
                      <div className="text-xs text-gray-500 mt-2 flex items-center">
                        <i className="fas fa-calendar mr-1"></i>
                        Activated: {new Date(form.activationDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-6" style={{ width: '15%' }}>
                  <div className="text-sm text-gray-900">
                    <span className="font-medium text-green-700">{form.accountManager}</span>
                  </div>
                </td>
                <td className="px-3 py-6" style={{ width: '20%' }}>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 flex items-center">
                      <i className="fas fa-satellite mr-2 text-blue-500 flex-shrink-0"></i>
                      <span className="whitespace-nowrap">{form.serviceLine}</span>
                    </div>
                    {form.kitNumber && (
                      <div className="text-sm text-gray-500 flex items-center mt-1">
                        <i className="fas fa-box mr-2 text-gray-400 flex-shrink-0"></i>
                        <span className="whitespace-nowrap">Kit: {form.kitNumber}</span>
                      </div>
                    )}
                    {form.oldServiceLine && (
                      <div className="text-xs text-gray-500 mt-1 flex items-center">
                        <i className="fas fa-arrow-left mr-1 flex-shrink-0"></i>
                        <span className="whitespace-nowrap">From: {form.oldServiceLine}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-6" style={{ width: '20%' }}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {form.packageLimit} GB Package
                    </div>
                    {form.oldPackage && (
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        Upgraded from: {form.oldPackage} GB
                      </div>
                    )}
                    <div className="mt-2">
                      <div className={`text-sm font-bold ${getUsageColor(form.currentUsage, form.packageLimit)}`}>
                        {form.currentUsage} GB used
                      </div>
                      <div className="flex items-center mt-1">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (form.currentUsage / form.packageLimit) * 100 >= 90 ? 'bg-red-500' :
                              (form.currentUsage / form.packageLimit) * 100 >= 75 ? 'bg-yellow-500' :
                              (form.currentUsage / form.packageLimit) * 100 >= 50 ? 'bg-orange-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min((form.currentUsage / form.packageLimit) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">
                          {(form.usagePercentage !== undefined ? form.usagePercentage : ((form.currentUsage / form.packageLimit) * 100)).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-6" style={{ width: '10%' }}>
                  <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${form.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-6" style={{ width: '15%' }}>
                  <div className="flex flex-col items-center space-y-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-lg ${getMonitorColor(form.monitorStatus)}`}>
                      {form.monitorStatus ?? 'Skipped'}
                    </span>
                    <div className="flex justify-center">
                      {form.notified100 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-bold rounded-lg bg-red-100 text-red-800">100%</span>
                      ) : form.notified75 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-bold rounded-lg bg-yellow-100 text-yellow-800">75%</span>
                      ) : null}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {displayedForms.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-users text-3xl text-green-600"></i>
          </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No sales forms found</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {search ? 'No forms match your search criteria.' : 
               user?.role === UserRole.ACCOUNT_MANAGER ? 
               'No forms are assigned to you yet.' :
               user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN ?
               'No forms have assigned Account Managers yet.' :
               'No forms are assigned to you yet.'}
          </p>
          <p className="text-sm text-gray-500">
              {user?.role === UserRole.ACCOUNT_MANAGER ? 
               'You can only see forms assigned to you.' :
               user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN ?
               'Only forms with assigned Account Managers are shown in the Sales dashboard.' :
               'You can only see forms assigned to you.'}
          </p>
        </div>
        )}
      </div>
    </div>
  );
};

export default Sales;
